import { useState } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiClient } from '@/api/client';
import { createOrGetWine } from '@/api/wines';
import { randomUUID } from '@/utils/uuid';

export interface ScannedWine {
  wineId: string;
  name: string | null;
  producer: string | null;
  region: string | null;
  country: string | null;
  vintage: number | null;
  grapes: string[];
  style: string | null;
  aiTastingNotes?: string | null;
  description?: string | null;
  foodPairings?: string[];
  servingTemp?: string | null;
  agingPotential?: string | null;
  priceRange?: string | null;
  body?: number | null;
  tannin?: number | null;
  acidity?: number | null;
  oak?: number | null;
  sweetness?: number | null;
  confidence: 'high' | 'medium' | 'low';
  rawOcrText: string;
  photoKey?: string;
  // Existing tasting data (pre-filled when wine has been logged before)
  catalogueWineId?: string;
  existingUserWineId?: string;
  existingRating?: number;
  existingNotes?: string;
  existingTastedAt?: string;
}

async function lookupExisting(
  name: string | null,
  producer: string | null,
  vintage: number | null,
): Promise<{ catalogueWineId?: string; existingUserWineId?: string; existingRating?: number; existingNotes?: string; existingTastedAt?: string }> {
  if (!name) return {};
  try {
    const { data: wines } = await apiClient.models.Wine.list({
      filter: {
        and: [
          { name: { eq: name } },
          ...(producer ? [{ producer: { eq: producer } }] : []),
          ...(vintage ? [{ vintage: { eq: vintage } }] : []),
        ],
      },
      limit: 1,
    });
    if (!wines?.length) return {};

    const catalogueWineId = wines[0].id;
    const { data: tastings } = await apiClient.models.UserWine.list({
      filter: { wineId: { eq: catalogueWineId } },
    });

    if (!tastings?.length) return { catalogueWineId };

    // Most recent tasting
    const latest = tastings.sort(
      (a, b) => new Date(b.tastedAt).getTime() - new Date(a.tastedAt).getTime()
    )[0];

    return {
      catalogueWineId,
      existingUserWineId: latest.id,
      existingRating: latest.rating ?? undefined,
      existingNotes: latest.notes ?? undefined,
      existingTastedAt: latest.tastedAt,
    };
  } catch (e) {
    console.warn('[WineScanner] existing lookup failed (non-fatal)', e);
    return {};
  }
}

export function useWineScanner() {
  const [scanning, setScanning] = useState(false);
  const [scannedWine, setScannedWine] = useState<ScannedWine | null>(null);
  const [error, setError] = useState('');

  async function scan(file: File, photoType: 'bottle' | 'label') {
    setScanning(true);
    setError('');
    console.log('[WineScanner] scan() started', { photoType, fileSize: file.size, fileType: file.type });
    try {
      const session = await fetchAuthSession();
      const identityId = session.identityId;
      console.log('[WineScanner] auth session fetched', { identityId: identityId ?? 'UNDEFINED — user may not be authenticated' });

      if (!identityId) throw new Error('Could not resolve identity ID — are you signed in?');

      const key = `photos/${identityId}/${photoType}s/${randomUUID()}.jpg`;
      console.log('[WineScanner] uploading to S3', { key });

      await uploadData({ path: key, data: file, options: { contentType: 'image/jpeg' } }).result;
      console.log('[WineScanner] S3 upload complete');

      console.log('[WineScanner] calling scanWineLabel mutation', { s3Key: key });
      const result = await apiClient.mutations.scanWineLabel({ s3Key: key });
      console.log('[WineScanner] scanWineLabel raw result', result);

      const raw = result.data;
      const wine = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ScannedWine | null;

      if (wine) {
        console.log('[WineScanner] scan succeeded', wine);
        // Look up existing catalogue entry and prior tasting
        const existing = await lookupExisting(wine.name, wine.producer, wine.vintage);
        console.log('[WineScanner] existing lookup result', existing);
        setScannedWine({ ...wine, photoKey: key, ...existing });
      } else {
        console.warn('[WineScanner] scan returned null/empty');
        result.errors?.forEach((e, i) => console.error(`[WineScanner] error[${i}]:`, JSON.stringify(e, null, 2)));
        const msg = result.errors?.[0]?.message ?? 'Unknown error';
        setError(`Scan error: ${msg}`);
      }
    } catch (err) {
      console.error('[WineScanner] scan error', err);
      setError(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }

  function reset(wine: ScannedWine | null) {
    setScannedWine(wine);
    setError('');
  }

  async function saveTasting(
    wine: ScannedWine,
    rating: number,
    notes: string,
    addToCellar: boolean,
    tastedAt: string,
  ) {
    if (!wine.name) throw new Error('Wine name is required');
    console.log('[WineScanner] saveTasting started', { wine, rating, addToCellar, tastedAt });

    // 1. Get/create Wine catalogue entry (skip if already known)
    const wineId = wine.catalogueWineId ?? await createOrGetWine({
      name: wine.name,
      producer: wine.producer,
      region: wine.region,
      country: wine.country,
      vintage: wine.vintage,
      grapes: wine.grapes,
      style: wine.style,
      description: wine.description,
      aiTastingNotes: wine.aiTastingNotes,
      foodPairings: wine.foodPairings,
      servingTemp: wine.servingTemp,
      agingPotential: wine.agingPotential,
      priceRange: wine.priceRange,
    });
    console.log('[WineScanner] wine catalogue id:', wineId);

    // 2. Update existing tasting or create new one
    if (wine.existingUserWineId) {
      console.log('[WineScanner] updating existing UserWine', wine.existingUserWineId);
      const updateResult = await apiClient.models.UserWine.update({
        id: wine.existingUserWineId,
        tastedAt,
        rating: rating > 0 ? rating : undefined,
        notes: notes || undefined,
        labelPhotoKey: wine.photoKey ?? undefined,
      });
      console.log('[WineScanner] UserWine update result', updateResult);
      if (updateResult.errors?.length) {
        throw new Error(updateResult.errors.map(e => e.message).join(', '));
      }
    } else {
      console.log('[WineScanner] creating new UserWine record');
      const createResult = await apiClient.models.UserWine.create({
        wineId,
        tastedAt,
        rating: rating > 0 ? rating : undefined,
        notes: notes || undefined,
        labelPhotoKey: wine.photoKey ?? undefined,
      });
      console.log('[WineScanner] UserWine create result', createResult);
      if (createResult.errors?.length) {
        throw new Error(createResult.errors.map(e => e.message).join(', '));
      }
    }

    // 3. Optionally add to cellar
    if (addToCellar) {
      console.log('[WineScanner] creating CellarEntry...');
      const cellarResult = await apiClient.models.CellarEntry.create({
        wineId,
        quantity: 1,
      });
      console.log('[WineScanner] CellarEntry result', cellarResult);
    }

    console.log('[WineScanner] saveTasting complete');
  }

  return { scanning, scannedWine, error, scan, reset, saveTasting };
}
